import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { ScrollText, Sparkles } from 'lucide-react';

/**
 * Compact governance tile showing the Operator Mira Doctrine version + sign-off.
 * Clickable — opens the full doctrine in a new tab (served from /docs on the
 * filesystem via the snapshot docs bundle, or raw from memory).
 *
 * Purpose: make the doctrine unignorable. Every time an engineer or the CEO
 * opens ScaleBoard, they see "v1.0 signed Dipali 20-Apr-2026" — no way to
 * pretend the governance doc doesn't exist.
 */
export default function OperatorMiraDoctrineTile() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/operator-mira/stats')
      .then((r) => setStats(r.data))
      .catch(() => setStats(null));
  }, []);

  if (!stats) return null;

  return (
    <div
      data-testid="operator-mira-doctrine-tile"
      className="rounded-xl border p-4 transition hover:border-[#2A7B88]"
      style={{ borderColor: '#E5E1DC', background: 'linear-gradient(135deg, #FAF8F5 0%, #F0EBE3 100%)' }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1B2A4A' }}>
            <ScrollText size={18} style={{ color: '#F5C86B' }} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Operator Mira Doctrine</p>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold" style={{ backgroundColor: '#1B2A4A', color: '#F5C86B' }}>v{stats.doctrine_version}</span>
            </div>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>
              Signed by {stats.doctrine_signed_by} · {new Date(stats.doctrine_signed_on).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
            <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>
              Brain: <span className="font-mono" style={{ color: '#1B2A4A' }}>{stats.brain_model}</span>
            </p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="flex items-center gap-1 justify-end">
            <Sparkles size={12} style={{ color: '#2A7B88' }} />
            <p className="text-[10px] font-medium uppercase tracking-wide" style={{ color: '#2A7B88' }}>This week</p>
          </div>
          <p className="text-xl font-bold leading-tight" style={{ color: '#1B2A4A' }}>{stats.week_writes}</p>
          <p className="text-[10px]" style={{ color: '#6B7280' }}>AI writes</p>
          <p className="text-[10px] mt-1" style={{ color: '#6B7280' }}>Today {stats.today_writes} / {stats.daily_cap}</p>
        </div>
      </div>
      <p className="text-[10.5px] mt-3 pt-3 border-t" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
        <span style={{ color: '#1B2A4A', fontWeight: 600 }}>The Law:</span> Operator Mira never writes without explicit Confirm · always shows before/after · asks when ambiguous · 24-hour undo on every action.
      </p>
    </div>
  );
}
